import React from 'react';
import { Button } from 'antd';
import { WithModal } from 'hocs/withModal';
import { useList } from 'context/ResourceProvider/hooks/useList';
import { transformResponse } from 'helpers/api';
import get from 'lodash/get';
import moment from 'moment';
import Status from 'components/Molecules/Status/index';
import QMASTable from 'components/Molecules/Table/index';
import Alert from 'components/Molecules/Alert/index';
import { PaperClipOutlined } from '@ant-design/icons';
import { ReactComponent as LightingIcon } from 'assets/icons/Light.svg';
import { ATooltip } from 'components/Antd/index';
import { getMomentDate } from 'util/date';
import cn from 'classnames';
import CellFactory from 'components/Molecules/Table/Cells/CellFactory';
import classes from './SelectTestData.module.scss';

const SelectTestData = (props, ref) => {
  const { resource, metadata, filters, defaultSelectedRows, complianceDataUrl } = props;
  const [selectRowKeys, setSelectRowKeys] = React.useState([]);

  const { data, loading, refetch } = useList({
    resource,
    meta: {
      transformResponse: [transformResponse],
      ...metadata,
    },
    filters,
  });

  const onSelectChange = (selectedRowKeys) => {
    setSelectRowKeys(selectedRowKeys);
  };

  React.useImperativeHandle(ref, () => {
    return {
      selectedRows: get(data, 'data', []).filter((row) => selectRowKeys.includes(row.id)),
    };
  });

  React.useEffect(() => {
    setSelectRowKeys(defaultSelectedRows);
  }, [defaultSelectedRows]);

  return (
    <div className={classes.selectTestData}>
      <Alert
        style={{ paddingRight: 0 }}
        banner
        visible
        type="info"
        showIcon
        icon={
          <span>
            <LightingIcon />
          </span>
        }
        message={
          <span className={classes.message}>
            To add or edit data, please visit{' '}
            <a href={complianceDataUrl} target="_blank" rel="noreferrer">
              Compliance Data
            </a>
            .
          </span>
        }
        action={
          <Button
            disabled={loading}
            loading={loading}
            type="primary"
            onClick={() => {
              refetch();
            }}>
            Reload Data
          </Button>
        }
      />
      <QMASTable
        pagination={false}
        loading={loading}
        dataSource={get(data, 'data', [])}
        rowKey={(record) => record.id}
        rowSelection={{
          type: 'checkbox',
          onChange: onSelectChange,
          selectedRowKeys: selectRowKeys,
        }}
        columns={[
          {
            title: 'Export Market',
            dataIndex: 'countryList',
            width: 120,
            render: (value) => {
              if (!value) return 'N/A';
              return value.name;
            },
          },
          {
            title: 'Test Item',
            dataIndex: 'testItem',
            width: '20%',
            render: (value) => {
              if (!value) return 'N/A';
              return value.name;
            },
          },
          {
            title: 'Citation',
            dataIndex: 'citation',
            render: (value) => {
              if (!value) return 'N/A';
              return value.name || 'N/A';
            },
          },
          {
            title: 'Report Id',
            dataIndex: 'reportId',
            render: (value) => {
              if (!value) return 'N/A';
              return value || 'N/A';
            },
          },
          {
            title: 'Issue Party',
            dataIndex: 'serviceProvider',
            width: 120,
            render: (value) => {
              if (!value) return 'N/A';
              return value.name;
            },
          },
          {
            title: 'Expiry Date',
            dataIndex: 'expiryDate',
            render: (value) => {
              if (!value) return 'N/A';
              const today = getMomentDate('', true);
              const tMomentVal = getMomentDate(value);
              const isRed = !!tMomentVal && tMomentVal.isSameOrBefore(today);
              return (
                <span
                  className={cn({
                    [classes.red]: isRed,
                  })}>
                  {moment(value).format('DD MMM YYYY')}
                </span>
              );
            },
          },
          {
            title: 'Test Date / Report Date',
            dataIndex: 'testDate',
            width: 120,
            render: (value) => {
              if (!value) return 'N/A';

              return <span>{moment(value).format('DD MMM YYYY')}</span>;
            },
          },
          {
            title: 'Result',
            dataIndex: 'result',
            render: (value) => {
              return (
                <Status
                  options={[
                    {
                      key: 'pass',
                      label: 'Passed',
                      color: 'success',
                      icon: null,
                    },
                    {
                      key: 'fail',
                      label: 'Failed',
                      color: 'error',
                      icon: null,
                    },
                  ]}
                  type="text"
                  value={value}
                />
              );
            },
          },
          {
            render: (_, record) => {
              if (record.documentLists.length === 0) return null;
              return (
                <ATooltip
                  dark
                  placement="topRight"
                  overlayClassName={classes.tooltipOverlay}
                  trigger={['click']}
                  title={
                    <div className={classes.documentList}>
                      {record.documentLists.map((doc) => (
                        <div key={doc.id}>
                          {CellFactory('QMASDocumentModalCell', {
                            wrapClassName: classes.overlayModal,
                            documents: [
                              {
                                ...doc,
                                name: doc.fileName,
                                ...doc.documentListIndex,
                                documentListId: doc.id,
                              },
                            ],
                            triggerElement: (
                              <Button className={classes.documentItem} type="text">
                                {doc.fileName}
                              </Button>
                            ),
                          })}
                        </div>
                      ))}
                    </div>
                  }>
                  <PaperClipOutlined style={{ fontSize: 16, color: '#3A4861' }} />
                </ATooltip>
              );
            },
          },
        ]}
      />
    </div>
  );
};
export default WithModal(React.forwardRef(SelectTestData));
